<template src="./send.html"></template>

<script>
import axios from 'axios'
  import { quoteDisplayTotal } from '../../../../helpers/quoteTotals'
  import {
    processUserFile,
    wouldExceedTotal,
    sumAttachmentBytes,
    MAX_TOTAL_ATTACHMENT_BYTES,
    formatFileSize
  } from '../../../../helpers/emailAttachments'

  export default {
    props: {
      parent_quote: {
        type: Object
      },
      parent_quote_items: {
        type: Array,
        default: function () { return [] }
      },
      parent_contact: {
        type: Object
      },
      parent_save: {
        type: Function
      }
    },
    computed: {
      display () {
        return this.$store.getters['setting/getDisplay'] || {}
      },
      documentAttachmentOptions () {
        const q = this.parent_quote || this.quote || {}
        const opts = Array.isArray(q.options) ? q.options.slice().sort((a, b) => a.option_index - b.option_index) : []
        const number = q.number || 'quote'
        if (!opts.length) {
          return [{
            option_index: 1,
            filename: number + '-1.pdf'
          }]
        }
        return opts.map((o) => ({
          option_index: o.option_index,
          filename: number + '-' + o.option_index + '.pdf'
        }))
      },
      attachmentCards () {
        const docs = (this.documentAttachments || []).map((d) => ({
          id: d.id,
          kind: 'document',
          option_index: d.option_index,
          filename: d.filename,
          sizeLabel: d.sizeLabel || 'PDF',
          thumbnail: '',
          size: d.size || 0
        }))
        return docs.concat(this.userAttachments || [])
      },
      canSend () {
        return this.attachmentCards.length > 0
      }
    },
    data: function () {
      return {
        email: {
          to: null,
          subject: null,
          message: null,
          type: 'quote',
          object: {}
        },
        quote: {},
        templates: {},
        sending: false,
        documentAttachments: [],
        userAttachments: [],
        attaching: false
      }
    },
    methods: {
      initDocumentAttachments () {
        this.documentAttachments = this.documentAttachmentOptions.map((o) => ({
          id: 'doc-' + o.option_index,
          option_index: o.option_index,
          filename: o.filename,
          sizeLabel: 'PDF',
          size: 0
        }))
      },
      onAttachClick () {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
          this.$refs.fileInput.click()
        }
      },
      confirmRemoveAttachment (card) {
        if (!card) return
        this.swal({
          title: 'Are you sure?',
          text: 'Remove ' + (card.filename || 'this attachment') + ' from the email?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, remove it'
        }).then((result) => {
          if (result && result.value) {
            this.removeAttachment(card)
          }
        })
      },
      removeAttachment (card) {
        if (!card) return
        if (card.kind === 'document') {
          this.documentAttachments = (this.documentAttachments || []).filter((d) => d.id !== card.id)
        } else {
          this.userAttachments = (this.userAttachments || []).filter((u) => u.id !== card.id)
        }
      },
      async onFilesSelected (files) {
        if (!files || !files.length) return
        this.attaching = true
        try {
          for (let i = 0; i < files.length; i++) {
            const file = files[i]
            let processed
            try {
              processed = await processUserFile(file)
            } catch (err) {
              this.errorNotification((err && err.message) || 'Could not add file')
              continue
            }
            const existing = this.userAttachments.concat(
              this.documentAttachments.map((d) => ({ size: d.size || 0 }))
            )
            if (wouldExceedTotal(existing, processed.size)) {
              this.errorNotification(
                'Attachments are too large to send. Total must be under ' +
                formatFileSize(MAX_TOTAL_ATTACHMENT_BYTES) + '.'
              )
              break
            }
            this.userAttachments = this.userAttachments.concat([processed])
          }
        } finally {
          this.attaching = false
        }
      },
      resolveContact () {
        const q = this.parent_quote || this.quote || {}
        const c = this.parent_contact || (q.contact || {}) || {}
        let first_name = c.first_name || q.contact_first_name || ''
        let last_name = c.last_name || q.contact_last_name || ''
        const contactName = (q.contact_name || '').trim()
        if (!first_name && !last_name && contactName) {
          const parts = contactName.split(/\s+/).filter(Boolean)
          if (parts.length === 1) {
            first_name = parts[0]
          } else if (parts.length > 1) {
            first_name = parts[0]
            last_name = parts.slice(1).join(' ')
          }
        }
        const company = c.company || q.contact_company || contactName || q.contact_name || ''
        const email = c.email || q.contact_email || ''
        const full_name = ([first_name, last_name].filter(Boolean).join(' ') || contactName || company || '').trim()
        return Object.assign({}, c, { first_name, last_name, company, email, full_name })
      },
      send: function () {
        const raw = (this.email.to && String(this.email.to).trim()) || ''
        const addresses = raw.split(',').map(function (e) { return e.trim() }).filter(Boolean)
        if (addresses.length === 0) {
          this.errorNotification('Please enter at least one recipient email address')
          return
        }
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const invalid = addresses.filter(function (e) { return !emailRe.test(e) })
        if (invalid.length > 0) {
          this.errorNotification('Invalid email address: ' + invalid[0])
          return
        }
        if (!this.canSend) {
          this.errorNotification('Add at least one attachment to send')
          return
        }
        const userBytes = sumAttachmentBytes(this.userAttachments)
        if (userBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
          this.errorNotification(
            'Attachments are too large to send. Total must be under ' +
            formatFileSize(MAX_TOTAL_ATTACHMENT_BYTES) + '.'
          )
          return
        }
        this.sending = true
        const optionIndexes = (this.documentAttachments || [])
          .map((d) => parseInt(d.option_index, 10))
          .filter((n) => Number.isFinite(n))
        const extra_attachments = (this.userAttachments || []).map((u) => ({
          filename: u.filename,
          content: u.content,
          type: u.type,
          size: u.size
        }))
        const payload = Object.assign({}, this.email, {
          option_indexes: optionIndexes,
          extra_attachments: extra_attachments
        })
        axios.post('/quote/' + this.quote.id + '/send', payload).then(response => {
          this.sending = false
          this.swal({
            type: 'success',
            title: 'Quote Sent',
            text: 'Your quote has been sent successfully'
          }).then(() => {
            this.$emit('close')
            this.$router.push('/').catch(() => {})
          })
        }).catch(err => {
          this.sending = false
          let msg = 'Failed to send email'
          if (err.response && err.response.data) {
            const d = err.response.data
            msg = (typeof d === 'string' ? d : (d.error != null ? d.error : msg))
          } else if (err.message) {
            msg = err.message
          }
          this.errorNotification(typeof msg === 'string' ? msg : JSON.stringify(msg))
        })
      },
      getTemplates: function () {
        axios.get('/api/email/template').then(response => {
          this.templates = response.data || {}
          this.setupEmailMessage()
        }).catch(() => {
          this.templates = { quote_message: '', invoice_message: '' }
          this.setupEmailMessage()
        })
      },
      loadDisplayThenTemplates: function () {
        axios.get('/api/setting/display').then(response => {
          if (response.data) {
            this.$store.commit('setting/setDisplay', response.data)
          }
          this.getTemplates()
        }).catch(() => this.getTemplates())
      },
      resolveQuoteTotalForPlaceholder: function (q) {
        if (!q) return '0.00'
        const items = Array.isArray(q.items) && q.items.length ? q.items : this.parent_quote_items
        return quoteDisplayTotal(q, items).toFixed(2)
      },
      setupEmailMessage: function () {
        const isInvoice = this.email.type === 'invoice'
        const raw = (isInvoice ? this.templates.invoice_message : this.templates.quote_message) || ''
        const q = this.parent_quote || this.quote || {}
        const contact = this.resolveContact()
        const display = this.$store.getters['setting/getDisplay'] || {}
        const totalStr = isInvoice
          ? (() => {
            const ta = q.total != null ? q.total : (q.total_amount != null ? q.total_amount : 0)
            return typeof ta === 'number' ? ta.toFixed(2) : (ta != null ? String(ta) : '0.00')
          })()
          : this.resolveQuoteTotalForPlaceholder(q)
        const quoteTerms = (q.terms != null && q.terms !== '') ? q.terms : (display.quote_terms != null ? display.quote_terms : (q.notes != null ? q.notes : ''))
        const invoiceTerms = (q.terms != null && q.terms !== '') ? q.terms : (display.invoice_terms != null ? display.invoice_terms : (q.notes != null ? q.notes : ''))
        let msg = raw
          .replace(/\[Customer First Name\]/g, contact.first_name || contact.full_name || contact.company || '')
          .replace(/\[Customer Last Name\]/g, contact.last_name || '')
          .replace(/\[Quote Number\]/g, q.number || '')
          .replace(/\[Number\]/g, q.number || '')
          .replace(/\[Quote Total Amount\]/g, '$' + totalStr)
          .replace(/\[Total Amount\]/g, '$' + totalStr)
          .replace(/\[Company Name\]/g, display.business_name || '')
        if (isInvoice) {
          msg = msg.replace(/\[Invoice-Terms\]/g, invoiceTerms).replace(/\[Quote-Terms\]/g, quoteTerms).replace(/\[Terms\]/g, invoiceTerms)
        } else {
          msg = msg.replace(/\[Quote-Terms\]/g, quoteTerms).replace(/\[Terms\]/g, quoteTerms)
        }
        this.email.message = msg
      }
    },
    watch: {
      parent_quote: {
        deep: true,
        handler: function () {
          this.quote = this.parent_quote || this.quote || {}
          this.initDocumentAttachments()
          this.setupEmailMessage()
        }
      },
      parent_quote_items: {
        deep: true,
        handler: function () {
          this.setupEmailMessage()
        }
      }
    },
    mounted: function () {
      this.quote = this.parent_quote || {}
      this.initDocumentAttachments()
      const contact = this.resolveContact()
      const display = this.$store.getters['setting/getDisplay'] || {}
      this.email.to = (contact.email || '').trim()
      this.email.from = (display.email || '').trim()
      const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
      const label = name || contact.company || ''
      this.email.subject = 'Quote ' + (this.quote.number || '') + (label ? ' – ' + label : '')
      this.email.object = this.quote
      this.loadDisplayThenTemplates()
    }
  }
</script>

<style scoped>
</style>
